// Root layout: header + sidebar + <Outlet/>, plus an auth guard that
// redirects between /login and / as needed.
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth, useLogout } from './features/auth/useAuth';
import { SessionProvider } from './contexts/SessionContext';
import { ActiveStreamsProvider } from './contexts/ActiveStreamsContext';
import { GraphStorageProvider } from './contexts/GraphStorageContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ZoomProvider } from './contexts/ZoomContext';
import { useGlobalTooltip } from './hooks/useGlobalTooltip';
import Sidebar from './features/sidebar/Sidebar';
import HeaderDropdown from './components/HeaderDropdown';
import CommandPalette, { openCommandPalette, paletteShortcutLabel } from './components/CommandPalette';
import malpediaLogo from './assets/malpedia-logo.png';
import malpediaLogoDark from './assets/malpedia-logo-dark.png';

export default function App() {
  return (
    <ThemeProvider>
      <ZoomProvider>
        <AppShell />
      </ZoomProvider>
    </ThemeProvider>
  );
}

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: user, isLoading } = useAuth();
  const logout = useLogout();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // The atlas is a full-bleed map: the sidebar folds to its rail there
  // unless someone opens it, and elsewhere keeps its own state.
  const mapPage = location.pathname.startsWith('/overview');
  const [mapSidebarOpen, setMapSidebarOpen] = useState(false);
  const { resolved } = useTheme();
  useGlobalTooltip();

  const onLoginPage = location.pathname === '/login';
  const authed = !!user;

  // Auth guard: bounce between /login and / depending on session.
  useEffect(() => {
    if (isLoading) return;
    if (!authed && !onLoginPage) {
      navigate('/login', { replace: true });
    } else if (authed && onLoginPage) {
      navigate('/', { replace: true });
    }
  }, [authed, isLoading, onLoginPage, navigate]);

  // "/" anywhere (outside a text field) opens search; /search itself
  // focuses its box instead.
  useEffect(() => {
    if (!authed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (location.pathname.startsWith('/search')) return;
      e.preventDefault();
      navigate('/search');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [authed, location.pathname, navigate]);

  const handleLogout = async () => {
    try {
      await logout.mutateAsync();
      toast.success('Signed out');
      navigate('/login', { replace: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Logout failed');
    }
  };

  // While auth is still being resolved, render a minimal placeholder so
  // child routes don't flash before the guard can act.
  if (isLoading) {
    return <div className="malpedia-app-loading">Loading…</div>;
  }

  return (
    <div className="malpedia-app-shell">
      {!onLoginPage && (
        <header className="malpedia-app-header">
          <Link to="/" className="malpedia-app-brand">
            <img
              src={resolved === 'dark' ? malpediaLogoDark : malpediaLogo}
              alt="Malpedia"
              className="malpedia-app-brand-logo"
            />
            <span>RAG</span>
          </Link>
          {user && (
            <div className="malpedia-app-header-right">
              <button type="button" className="malpedia-cmdk-trigger" onClick={openCommandPalette} aria-label="Search and commands" aria-keyshortcuts="Meta+K Control+K">
                <span className="malpedia-cmdk-trigger-text">Search…</span>
                <kbd>{paletteShortcutLabel()}</kbd>
              </button>
              <HeaderDropdown
                user={user}
                onLogout={handleLogout}
                logoutPending={logout.isPending}
              />
            </div>
          )}
        </header>
      )}
      {onLoginPage ? (
        <main className="malpedia-app-main">
          <Outlet />
        </main>
      ) : (
        <SessionProvider>
          {/* Above the Outlet: in-flight chat turns outlive chat switches
              and route changes, and re-attach after a reload. */}
          <ActiveStreamsProvider>
            <GraphStorageProvider>
              <div className="malpedia-app-body">
                <Sidebar
                  collapsed={mapPage ? !mapSidebarOpen : sidebarCollapsed}
                  onToggleCollapse={() => (mapPage ? setMapSidebarOpen((v) => !v) : setSidebarCollapsed((v) => !v))}
                />
                <main className="malpedia-app-main">
                  <Outlet />
                </main>
              </div>
              <CommandPalette />
            </GraphStorageProvider>
          </ActiveStreamsProvider>
        </SessionProvider>
      )}
    </div>
  );
}
